import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DeviceAddService } from "@app/device/services/device-add.service";
import { RouterService } from "@app/root-store/router.service";
import { isEmbedded } from "@app/shared/resources/embedded-mobile-checker";
import { EquatorEventsService } from "@app/shared/services/equator-api/equator-events";
import { MatIconLoaderService } from "@app/shared/services/mat-icon-loader.service";
import { Observable, Subscription, catchError, exhaustMap, filter, of, shareReplay, take } from "rxjs";
import { ClareDialogComponent } from "../clare-dialog/clare-dialog.component";

@Component({
    selector: "app-embedded-mobile-header",
    templateUrl: "./embedded-mobile-header.component.html",
    styleUrls: ["./embedded-mobile-header.component.css"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbeddedMobileHeaderComponent implements OnInit, OnDestroy {
    @Input() backText: string;
    @Input() backAction: string | (() => void);
    @Input() queryParams;
    @Input() isTitleEditable = false;
    @Input() pageTitle: string;
    @Output() updateTitle = new EventEmitter();
    moreZwave = new EventEmitter();
    backToDevices = new EventEmitter();
    uuid$: Observable<string> = this.routerService.getUuid().pipe(shareReplay(1));
    isOpen = false;

    private subs: Subscription[] = [];

    constructor(
        private readonly routerService: RouterService,
        private readonly cdr: ChangeDetectorRef,
        private readonly equatorEventsService: EquatorEventsService,
        private readonly ngZone: NgZone,
        matIconLoader: MatIconLoaderService,
        public dialog: MatDialog,
        private readonly deviceAddService: DeviceAddService
    ) {
        matIconLoader.importIcons([[`cc-chevron-left`, `chevron-left`]]);
    }

    ngOnInit() {
        (window as any).androidBackButtonHit = () => {
            this.ngZone.run(() => {
                this.back();
            });
        };

        if (!this.pageTitle) {
            this.subs.push(
                this.routerService.getPageTitle().subscribe(title => {
                    this.pageTitle = title;
                    if (this.cdr) {
                        this.cdr.detectChanges();
                    }
                })
            );
        }

        this.subs.push(
            this.routerService.updateActiveRoutes().subscribe(),
            this.routerService.getBackAction().subscribe(action => {
                this.backAction = action;
                if (this.cdr) {
                    this.cdr.detectChanges();
                }
            }),
            this.routerService.getQueryParams().subscribe(queryParams => {
                this.queryParams = queryParams;
                if (this.cdr) {
                    this.cdr.detectChanges();
                }
            })
        );
    }

    ngOnDestroy() {
        this.subs.forEach(sub => sub.unsubscribe());
    }

    get isEmbedded() {
        return isEmbedded();
    }

    back() {
        if (this.queryParams?.addedZwave === "true") {
            this.displayBackToZWaveDialog();
        } else {
            this.closeWebView();
        }
    }

    closeWebView() {
        if (typeof this.backAction === "string") {
            return this.routerService.navigateToUrl([this.backAction], null, this.queryParams);
        }
        if (this.backAction) {
            return this.backAction();
        }

        this.uuid$
            .pipe(
                filter(uuid => !!uuid),
                exhaustMap(uuid => this.equatorEventsService.refreshUI(uuid)),
                take(1),
                catchError(err => of(err))
            )
            .subscribe();
        return (window as any).mchObserver.postMessage("closeWebView");
    }

    goToAddZwave() {
        this.deviceAddService.getChDeviceMetadataSorted().subscribe(categories => {
            const zwaveCategoryIndex = categories.findIndex(category => category.key === "Z-Wave");
            this.uuid$
                .pipe(take(1))
                .subscribe(uuid =>
                    this.routerService.navigateToUrl([`myaccount/locations/${uuid}/devices/add1/method/${zwaveCategoryIndex}`])
                );
        });
    }

    displayBackToZWaveDialog() {
        this.moreZwave.subscribe(event => {
            this.goToAddZwave();
            event?.close();
        });
        this.backToDevices.subscribe(event => {
            this.closeWebView();
        });

        const data = {
            message: "Would you like to add another Z-Wave device?",
            buttons: [
                {
                    text: "Back to Devices",
                    buttonColor: "secondary",
                    method: this.backToDevices,
                },
                {
                    text: "Add Device",
                    buttonColor: "primary",
                    method: this.moreZwave,
                },
            ],
        };
        const config = { data };
        if (!this.dialog.openDialogs?.length) {
            this.dialog.open(ClareDialogComponent, config);
        }
    }

    onTitleUpdate(event) {
        this.updateTitle.emit(event.target.value);
    }
}
